import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '../../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';

export interface RefPickerOption {
    id: string;
    label: string;
    description?: string;
}

interface RefVariablePickerProps {
    options: RefPickerOption[];
    selectedId?: string;
    freeTextValue: string;
    canClear?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    onSelect: (id: string) => void;
    onSelectFreeText: (text: string) => void;
    onClear: () => void;
}

export function RefVariablePicker({
    options,
    selectedId,
    freeTextValue,
    canClear = true,
    placeholder,
    searchPlaceholder,
    emptyText,
    onSelect,
    onSelectFreeText,
    onClear,
}: RefVariablePickerProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const displayValue = selectedId
        ? options.find((o) => o.id === selectedId)?.label ?? ''
        : freeTextValue ?? '';

    const filtered = useMemo(
        () =>
            options.filter((o) =>
                o.label.toLowerCase().includes(search.toLowerCase())
            ),
        [search, options]
    );

    const showFreeTextOption =
        search.trim().length > 0 &&
        !options.some((o) => o.label.toLowerCase() === search.toLowerCase());

    return (
        <div className="flex items-center gap-2">
            <Popover
                open={open}
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setSearch('');
                }}
            >
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            'flex-1 justify-between font-normal bg-background',
                            !displayValue && 'text-muted-foreground'
                        )}
                    >
                        <span className="truncate">
                            {displayValue || placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={searchPlaceholder}
                            value={search}
                            onValueChange={setSearch}
                        />
                        <CommandList>
                            <CommandEmpty>{emptyText}</CommandEmpty>
                            {filtered.length > 0 && (
                                <CommandGroup>
                                    {filtered.map((option) => (
                                        <CommandItem
                                            key={option.id}
                                            value={option.id}
                                            onSelect={() => {
                                                onSelect(option.id);
                                                setSearch('');
                                                setOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4 shrink-0',
                                                    selectedId === option.id
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            <span>{option.label}</span>
                                            {option.description && (
                                                <span className="ml-1.5 text-xs text-muted-foreground">
                                                    {option.description}
                                                </span>
                                            )}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                            {showFreeTextOption && (
                                <CommandGroup>
                                    <CommandItem
                                        value={`__freetext__${search}`}
                                        onSelect={() => {
                                            onSelectFreeText(search.trim());
                                            setSearch('');
                                            setOpen(false);
                                        }}
                                    >
                                        <span className="text-muted-foreground mr-1">
                                            Use
                                        </span>
                                        &ldquo;{search.trim()}&rdquo;
                                    </CommandItem>
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {canClear && (selectedId || freeTextValue) && (
                <Button
                    type="button"
                    onClick={onClear}
                    variant="ghost"
                    size="icon"
                    className="hover:text-foreground shrink-0"
                    aria-label="Clear"
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
}
